import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { cartStore, checkoutStore, mainStore, orderStore } from '~/stores';
import { PICKUP_ALCOHOL_COUPON_CODE } from '~/stores/constants';
import { ApplyPromocodeCallerSource } from '~/stores/PromotionsStore/interfaces';

export interface UsePromocodeOptions {
  checkPercentageType?: boolean;
  watchPaidWithDiscount?: boolean;
}

export interface UsePromocodeReturn {
  promocodeVal: string;
  setPromocodeVal: (value: string) => void;
  warningMsg: string;
  isLoading: boolean;
  handlePromocodeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleDelPromocode: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  checkPromocode: (val: string) => void;
  promocodeExists: boolean;
  hasLocalError: boolean;
  hasLocalSuccess: boolean;
}

export const usePromocode = (
  options: UsePromocodeOptions = {},
): UsePromocodeReturn => {
  const { checkPercentageType = false, watchPaidWithDiscount = false } =
    options;
  const { t } = useTranslation();

  const [promocodeVal, setPromocodeVal] = useState(
    cartStore.promocode.value || '',
  );
  const [warningMsg, setWarningMsg] = useState('');

  // New local state for error and success tracking
  const [hasLocalError, setHasLocalError] = useState(false);
  const [hasLocalSuccess, setHasLocalSuccess] = useState(false);
  const lastCheckedValueRef = useRef<string>('');

  // Sync store value to local state when it becomes available after mount
  useEffect(() => {
    const storeValue = cartStore.promocode.value;
    if (storeValue && !promocodeVal) {
      setPromocodeVal(storeValue);
    }
  }, [cartStore.promocode.value]);

  const isLoading = cartStore.promotionStore.applyPromocodeMeta.isLoading;

  const handlePromocodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value.trim();
    setPromocodeVal(newValue);

    // Reset local error/success states when user starts typing something different
    if (newValue !== lastCheckedValueRef.current) {
      setHasLocalError(false);
      setHasLocalSuccess(false);
    }
  };

  const handleDelPromocode = () => {
    setPromocodeVal('');
    cartStore.promotionStore.resetPromocode();

    // Reset local states when deleting promocode
    setHasLocalError(false);
    setHasLocalSuccess(false);
    lastCheckedValueRef.current = '';
  };

  const checkPromocode = (val: string) => {
    if (!val.length) {
      return;
    }

    // Store the value being checked
    lastCheckedValueRef.current = val;

    cartStore.promotionStore
      .applyPromocode(val, ApplyPromocodeCallerSource.CART)
      .then((result: boolean) => {
        if (cartStore.promotionStore.promocode.deliveryMethod) {
          checkoutStore.setDeliveryMethod(
            cartStore.promotionStore.promocode.deliveryMethod,
          );
        }

        // Set local success/error based on the result
        // Use promotionStore.promocode instead of cartStore.promocode
        if (result) {
          setHasLocalSuccess(true);
          setHasLocalError(false);
        } else {
          setHasLocalError(true);
          setHasLocalSuccess(false);
        }
      })
      .catch(() => {
        // Handle error case
        setHasLocalError(true);
        setHasLocalSuccess(false);
      });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    checkPromocode(promocodeVal);
  };

  const promocodeExists =
    cartStore.promocode.success !== null &&
    promocodeVal === cartStore.promocode.value;

  useEffect(() => {
    if (
      !cartStore.totalCartPrice.amountLeftToDiscount ||
      mainStore.isZero(cartStore.totalCartPrice.amountLeftToDiscount)
    ) {
      setWarningMsg('');
      return;
    }

    const shouldShowWarning = checkPercentageType
      ? !cartStore.isPromoCodeApplied &&
        cartStore.promocode.coupon?.type &&
        cartStore.promocode.coupon?.type !== 'PERCENTAGE'
      : !cartStore.isPromoCodeApplied && cartStore.promocode.coupon?.type;

    if (shouldShowWarning) {
      let msg =
        cartStore.promocode.coupon?.code === PICKUP_ALCOHOL_COUPON_CODE
          ? 'errors:promocodePriceLessNoAlcohol'
          : 'errors:promocodePriceLess';

      if (!company.isExceptionAlcohol) {
        msg = 'errors:promocodePriceLessNoAlcohol';
      }

      setWarningMsg(
        t(msg, {
          amountLeft: mainStore.addCurrencySymbol(
            cartStore.totalCartPrice.amountLeftToDiscount,
          ),
          discount: cartStore.formatPromocodeDiscountAmount,
        }),
      );
    } else {
      setWarningMsg('');
    }
  }, [cartStore.totalCartPrice, checkPercentageType]);

  const etaCost = useMemo(
    () => orderStore.etaCalculation?.cost,
    [
      orderStore.etaCalculation?.cost?.delivery,
      orderStore.etaCalculation?.cost?.subtotal,
      orderStore.etaCalculation?.cost?.total,
    ],
  );

  const lastAppliedPromocodeRef = useRef<string | null>(null);

  useEffect(() => {
    if (cartStore.isPromoCodeApplied) {
      lastAppliedPromocodeRef.current = cartStore.promocode.value;
      return;
    }

    if (
      cartStore.promotionStore.applyPromocodeMeta.isLoading ||
      cartStore.promotionStore.applyPromocodeMeta.hasError
    ) {
      return;
    }

    if (!cartStore.promocode.value) {
      lastAppliedPromocodeRef.current = null;
      return;
    }

    if (lastAppliedPromocodeRef.current === cartStore.promocode.value) {
      return;
    }

    lastAppliedPromocodeRef.current = cartStore.promocode.value;
    checkPromocode(cartStore.promocode.value);
  }, [etaCost]);

  useEffect(() => {
    if (!watchPaidWithDiscount) {
      return;
    }

    if (
      !cartStore.promocode.value ||
      cartStore.isPromoCodeApplied ||
      cartStore.promotionStore.applyPromocodeMeta.isLoading
    ) {
      return;
    }

    Boolean(cartStore.promocode.value) &&
      cartStore.promotionStore.applyPromocode(
        cartStore.promocode.value,
        ApplyPromocodeCallerSource.CART,
      );
  }, [cartStore.totalCartPrice.paidWithDiscount, watchPaidWithDiscount]);

  return {
    promocodeVal,
    setPromocodeVal,
    warningMsg,
    isLoading,
    handlePromocodeChange,
    handleDelPromocode,
    handleSubmit,
    checkPromocode,
    promocodeExists,
    hasLocalError,
    hasLocalSuccess,
  };
};
